import type { Client } from '@optimizely/optimizely-sdk';
import {
    createInstance,
    setLogger as setOptimizelyLogLevel
} from '@optimizely/optimizely-sdk/dist/optimizely.lite.min.js';
import { getOptimizelyOverrides } from './optimizelyOverrides';
import {
    combineMaps,
    flattenMap,
    OptimizelyState,
    OptimizelyStateMap
} from './state';

/* written out at build time in next.config.js. We must import dynamically
with a fallback so that it can work in a unit test environment */
export async function getOptimizelyDatafile() {
    /* when running unit tests we do not want to return the datafile value as it is dynamic. however we can mock with an env var to allow some tests */
    if (process.env['NODE_ENV'] === 'test') {
        return JSON.parse(
            process.env['MOCK_OPTIMIZELY_DATAFILE_STR'] ?? '{ "version:" "4" }'
        );
    }
    try {
        // this file could be missing if the build has not run yet, stop
        // ts complaining about "Cannot find module"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return await import('./__BUILD_TIME_DATAFILE.json');
    } catch (e) {
        return { version: '4' };
    }
}

type SimpleRequestObject = {
    cookies: Record<string, string | undefined>;
    headers: Record<string, number | string | string[] | undefined>;
    url?: string;
};

const VERCEL_EDGE_CLIENT_ENGINE = 'javascript-sdk/vercel-edge';

/* this file should only ever execute on the server */
if (undefined === typeof window && setOptimizelyLogLevel) {
    setOptimizelyLogLevel(null);
}

export const createOptimizelyClient = async (): Promise<Client> => {
    const datafile = await getOptimizelyDatafile();
    return createInstance({
        datafile,
        eventDispatcher: {
            // this is a no-op because we do not dispatch any events, experiments or flags must be "activated" and this happens on the client, we only read here so no need to dispatch
            dispatchEvent: () => void 0
        },
        clientEngine: VERCEL_EDGE_CLIENT_ENGINE
    }) as Client;
};

/**
 * Returns relevant optimizely state for a given request and a promise for
 * event flushing that should be awaited according to request lifecycle.
 * Must be called on server, either in data-fetching methods, api or middleware.
 *
 * **userId must exist:** either provided at `req.cookies['ajs_anonymous_id']`
 * or generated and passed as param. If generating ensure persisted to session
 * with Set-Cookie header or writing to document.cookie
 */
export const getOptimizelyState = async (
    req: SimpleRequestObject,
    anonId?: string
): Promise<
    [optimizelyState: OptimizelyState, flushPromise: Promise<unknown>]
> => {
    const params = parseParams(req?.url ?? '');
    const ua = parseHeader(req?.headers?.['user-agent']);
    const cookies = req.cookies ?? {};
    const userId = anonId ?? cookies['ajs_anonymous_id'];

    const allOverridesMap: OptimizelyStateMap = getOptimizelyOverrides(
        req.cookies,
        params
    );

    if (!userId) {
        return [flattenMap(allOverridesMap), Promise.resolve()];
    }

    const attributes = ua ? { $opt_user_agent: ua } : undefined;

    const optimizelyClientInstance = await createOptimizelyClient();

    const { success } = await optimizelyClientInstance.onReady({
        timeout: 100
    });

    if (!success) {
        return [flattenMap(allOverridesMap), optimizelyClientInstance.close()];
    }

    const { experimentsMap, featuresMap } =
        optimizelyClientInstance.getOptimizelyConfig() ?? {};

    const scopedStateMap: OptimizelyStateMap = {
        experiments: {},
        features: {}
    };

    for (const key in featuresMap) {
        const enabled = optimizelyClientInstance.isFeatureEnabled(
            key,
            userId,
            attributes
        );
        if (enabled) scopedStateMap.features[key] = true;
    }

    for (const key in experimentsMap) {
        const variant = optimizelyClientInstance.getVariation(
            key,
            userId,
            attributes
        );
        if (variant !== null) scopedStateMap.experiments[key] = variant;
    }

    return [
        flattenMap(combineMaps(scopedStateMap, allOverridesMap)),
        optimizelyClientInstance.close()
    ];
};

/**
 * TODO: remove in favour of having the withSSROptimizely wrapper adding it to
 * context by default
 */
export const getOptimizelyFeatureFlagOnServer = async (
    req: SimpleRequestObject,
    featureKey: string,
    anonId?: string
) => {
    const [state, flushPromise] = await getOptimizelyState(req, anonId);
    await flushPromise;
    return state.includes(featureKey);
};

function parseParams(url: string): Record<string, string> {
    const params: Record<string, string> = {};
    try {
        const { searchParams } = new URL(url, 'https://www.zoopla.co.uk');
        for (const [key, value] of (
            searchParams as unknown as { entries(): Iterable<[string, string]> }
        ).entries()) {
            params[key] = value;
        }
        return params;
    } catch (e) {
        return params;
    }
}

function parseHeader(headerVal?: string | number | string[]) {
    return Array.isArray(headerVal) ? headerVal[0] : headerVal;
}
